import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Paper,
  Stack,
  Divider,
  TableContainer,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HistoryIcon from "@mui/icons-material/History";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPerfect from "../../../assets/Logo.png";
import { BRANDING } from "../../../config/branding";

import {
  getReparaciones,
  cambiarEstado,
  getHistorial
} from "../../../services/modules/SeguimientoReparacion";

// -------- PDF ----------
const generarPDF = (reparacion) => {
  const doc = new jsPDF();
  const fechaActual = reparacion.fecha_entrega
    ? new Date(reparacion.fecha_entrega).toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    : "";

  const img = new Image();
  img.src = logoPerfect;

  img.onload = () => {
    doc.addImage(img, "PNG", 10, 10, 60, 30);

    doc.setFontSize(18);
    doc.text("COMPROBANTE", 105, 20, { align: "center" });
    doc.text("REPARACIÓN", 105, 28, { align: "center" });

    doc.setFontSize(12);
    const companyX = 15;
    const companyY = 50;
    doc.text(BRANDING.pdf.name, companyX, companyY);
    BRANDING.pdf.addressLines.forEach((line, index) => {
      doc.text(line, companyX, companyY + 6 * (index + 1));
    });
    doc.text(
      `Teléfono: ${BRANDING.phoneText}`,
      companyX,
      companyY + 6 * (BRANDING.pdf.addressLines.length + 1)
    );

    doc.text(`Cliente: ${reparacion.cliente}`, 140, 45);
    if (reparacion.telefono) {
      doc.text(`Teléfono: ${reparacion.telefono}`, 140, 51);
      doc.text(`Fecha: ${fechaActual}`, 140, 57);
      doc.text(`Reparación ID: ${reparacion.id_reparacion}`, 140, 63);
    } else {
      doc.text(`Fecha: ${fechaActual}`, 140, 51);
      doc.text(`Reparación ID: ${reparacion.id_reparacion}`, 140, 57);
    }

    autoTable(doc, {
      startY: 70,
      head: [["Equipo", "Problema Reportado", "Estado", "Costo"]],
      body: [[
        reparacion.equipo,
        reparacion.problema_reportado,
        reparacion.estado,
        `Q${parseFloat(reparacion.costo || 0).toFixed(2)}`
      ]],
      styles: {
        halign: "center",
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
        cellPadding: 3,
        textColor: 20
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255],
        halign: "center"
      },
      theme: "grid"
    });

    const yFinal = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text("Gracias por confiar en nuestros servicios.", 15, yFinal);
    doc.text("Recibí Conforme", 105, yFinal + 20, { align: "center" });
    doc.text("F._______________________________________", 105, yFinal + 30, { align: "center" });

    doc.save(`Reparacion_${reparacion.id_reparacion}.pdf`);
  };
};

const SeguimientoReparacion = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [reparaciones, setReparaciones] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [estadosActuales, setEstadosActuales] = useState({});
  const [busquedaNombre, setBusquedaNombre] = useState("");
  const [busquedaEstado, setBusquedaEstado] = useState("");

  const [observacionesActuales, setObservacionesActuales] = useState({});
  const [costos, setCostos] = useState({});
  const [fechasEntrega, setFechasEntrega] = useState({});
  const [modalHistorialOpen, setModalHistorialOpen] = useState(false);

  const estados = ["Ingresado", "Diagnosticado", "Reparado", "Entregado"];

  useEffect(() => {
    cargarReparaciones();
  }, []);

  const cargarReparaciones = async () => {
    const data = await getReparaciones();
    setReparaciones(Array.isArray(data) ? data : []);
  };

  const handleEstadoChange = async (id_reparacion) => {
    const nuevoEstado = estadosActuales[id_reparacion] || "";
    const observacion = observacionesActuales[id_reparacion] || "";
    const costo = costos[id_reparacion] ?? null; // puede ser null
    const fechaEnt = fechasEntrega[id_reparacion] || null;

    if (!nuevoEstado) return alert("Seleccione un estado");

    await cambiarEstado(id_reparacion, nuevoEstado, observacion, costo, fechaEnt);
    alert("Estado actualizado correctamente");

    setEstadosActuales((prev) => ({ ...prev, [id_reparacion]: "" }));
    setObservacionesActuales((prev) => ({ ...prev, [id_reparacion]: "" }));
    setCostos((prev) => ({ ...prev, [id_reparacion]: "" }));
    setFechasEntrega((prev) => ({ ...prev, [id_reparacion]: "" }));
    cargarReparaciones();
  };

  const mostrarHistorial = async (id_reparacion) => {
    const data = await getHistorial(id_reparacion);
    setHistorial(Array.isArray(data) ? data : []);
    setModalHistorialOpen(true);
  };

  const reparacionesFiltradas = useMemo(
    () =>
      (reparaciones || []).filter((r) => {
        const coincideNombre = String(r.cliente || "")
          .toLowerCase()
          .includes((busquedaNombre || "").toLowerCase());
        const coincideEstado = busquedaEstado ? r.estado === busquedaEstado : true;
        return coincideNombre && coincideEstado;
      }),
    [reparaciones, busquedaNombre, busquedaEstado]
  );

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h5" gutterBottom>
        Seguimiento de Reparaciones
      </Typography>

      {/* Filtros */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Buscar por nombre del cliente"
            fullWidth
            value={busquedaNombre}
            onChange={(e) => setBusquedaNombre(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Filtrar por estado"
            fullWidth
            value={busquedaEstado}
            onChange={(e) => setBusquedaEstado(e.target.value)}
          >
            <MenuItem value="">Todos</MenuItem>
            {estados.map((estado) => (
              <MenuItem key={estado} value={estado}>
                {estado}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Móvil: tarjetas | Desktop: tabla */}
      {isXs ? (
        <Box>
          {reparacionesFiltradas.length === 0 && (
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography align="center" color="text.secondary">
                Sin reparaciones
              </Typography>
            </Paper>
          )}

          {reparacionesFiltradas.map((r) => {
            const estadoSeleccionado = estadosActuales[r.id_reparacion] || "";
            const mostrarEntrega = estadoSeleccionado === "Entregado";
            return (
              <Paper key={r.id_reparacion} sx={{ p: 2, mb: 1.5, borderRadius: 2, boxShadow: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {r.cliente}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {r.equipo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Estado actual: <strong>{r.estado}</strong>
                    </Typography>
                  </Box>
                  <IconButton onClick={() => mostrarHistorial(r.id_reparacion)}>
                    <HistoryIcon />
                  </IconButton>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Nuevo estado"
                      size="small"
                      fullWidth
                      value={estadoSeleccionado}
                      onChange={(e) =>
                        setEstadosActuales((prev) => ({
                          ...prev,
                          [r.id_reparacion]: e.target.value
                        }))
                      }
                    >
                      {estados.map((estado) => (
                        <MenuItem key={estado} value={estado}>
                          {estado}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label="Observaciones"
                      size="small"
                      fullWidth
                      value={observacionesActuales[r.id_reparacion] || ""}
                      onChange={(e) =>
                        setObservacionesActuales((prev) => ({
                          ...prev,
                          [r.id_reparacion]: e.target.value
                        }))
                      }
                    />
                  </Grid>

                  {mostrarEntrega && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Fecha de entrega"
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={fechasEntrega[r.id_reparacion] || ""}
                          onChange={(e) =>
                            setFechasEntrega((prev) => ({
                              ...prev,
                              [r.id_reparacion]: e.target.value
                            }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Costo (Q)"
                          size="small"
                          type="number"
                          placeholder="0.00"
                          fullWidth
                          value={costos[r.id_reparacion] || ""}
                          onChange={(e) =>
                            setCostos((prev) => ({
                              ...prev,
                              [r.id_reparacion]: e.target.value
                            }))
                          }
                        />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                  {r.estado === "Entregado" && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        generarPDF({
                          ...r,
                          fecha_entrega: fechasEntrega[r.id_reparacion] || r.fecha_entrega
                        })
                      }
                    >
                      Imprimir
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={() => handleEstadoChange(r.id_reparacion)}
                  >
                    Cambiar estado
                  </Button>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Equipo</TableCell>
                <TableCell>Estado Actual</TableCell>
                <TableCell>Nuevo Estado</TableCell>
                <TableCell>Observaciones</TableCell>
                <TableCell>Fecha Entrega</TableCell>
                <TableCell>Costo (Q)</TableCell>
                <TableCell>Actualizar</TableCell>
                <TableCell>Historial</TableCell>
                <TableCell>Imprimir</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reparacionesFiltradas.map((r) => {
                const estadoSeleccionado = estadosActuales[r.id_reparacion] || "";
                const mostrarEntrega = estadoSeleccionado === "Entregado";
                return (
                  <TableRow key={r.id_reparacion}>
                    <TableCell>{r.cliente}</TableCell>
                    <TableCell>{r.equipo}</TableCell>
                    <TableCell>{r.estado}</TableCell>

                    <TableCell>
                      <Select
                        size="small"
                        value={estadoSeleccionado}
                        onChange={(e) =>
                          setEstadosActuales((prev) => ({
                            ...prev,
                            [r.id_reparacion]: e.target.value
                          }))
                        }
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">
                          <em>Seleccione…</em>
                        </MenuItem>
                        {estados.map((estado) => (
                          <MenuItem key={estado} value={estado}>
                            {estado}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>

                    <TableCell>
                      <TextField
                        size="small"
                        value={observacionesActuales[r.id_reparacion] || ""}
                        onChange={(e) =>
                          setObservacionesActuales((prev) => ({
                            ...prev,
                            [r.id_reparacion]: e.target.value
                          }))
                        }
                        fullWidth
                      />
                    </TableCell>

                    <TableCell>
                      {mostrarEntrega ? (
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={fechasEntrega[r.id_reparacion] || ""}
                          onChange={(e) =>
                            setFechasEntrega((prev) => ({
                              ...prev,
                              [r.id_reparacion]: e.target.value
                            }))
                          }
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell>
                      {mostrarEntrega ? (
                        <TextField
                          size="small"
                          type="number"
                          placeholder="0.00"
                          value={costos[r.id_reparacion] || ""}
                          onChange={(e) =>
                            setCostos((prev) => ({
                              ...prev,
                              [r.id_reparacion]: e.target.value
                            }))
                          }
                          fullWidth
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>

                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleEstadoChange(r.id_reparacion)}
                      >
                        Cambiar
                      </Button>
                    </TableCell>

                    <TableCell>
                      <IconButton onClick={() => mostrarHistorial(r.id_reparacion)}>
                        <HistoryIcon />
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      {r.estado === "Entregado" ? (
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() =>
                            generarPDF({
                              ...r,
                              fecha_entrega: fechasEntrega[r.id_reparacion] || r.fecha_entrega
                            })
                          }
                        >
                          Imprimir
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Modal historial */}
      <Dialog
        open={modalHistorialOpen}
        onClose={() => setModalHistorialOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Historial de Reparación</DialogTitle>
        <DialogContent dividers>
          {(historial || []).map((h, index) => (
            <Box key={index} mb={2}>
              <Typography variant="body2">
                <strong>Estado:</strong> {h.estado}
              </Typography>
              <Typography variant="body2">
                <strong>Fecha:</strong>{" "}
                {new Date(h.fecha).toLocaleString()}
              </Typography>
              <Typography variant="body2">
                <strong>Observaciones:</strong> {h.observaciones}
              </Typography>
              <Divider sx={{ mt: 1 }} />
            </Box>
          ))}
          {(!historial || historial.length === 0) && (
            <Typography color="text.secondary">Sin movimientos</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalHistorialOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SeguimientoReparacion;
